import React from "react";
import {
  TextField,
  Button,
  Typography,
  LinearProgress,
  Box,
  Container,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Fade,
  Slide,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useState } from "react";
import AuthServices from "../src/services/AuthServices";
import logo from "./assets/logo.webp";

const LoginPage = () => {
  const [response, setResponse] = useState();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showProgress, setShowProgress] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setShowProgress(true);
    setResponse(null);

    if (username.length > 0 && password.length > 0) {
      try {
        const data = await AuthServices.ValidateUser({
          username,
          password,
        });
        if (data && data.status === true) {
          const success = AuthServices.handleLoginSuccess(data.result, true);
          if (success) {
            setResponse({ severity: "success", message: data.message || "Login successful." });
            // Full page navigation so app reinitializes with new cookie (avoids "works only after hard refresh")
            window.location.assign("/main/dashboard");
            return;
          }
        }
        setResponse({
          severity: "error",
          message: (data && data.message) || "Login failed. Please try again.",
        });
      } catch (error) {
        setResponse({
          severity: "error",
          message: "An error occurred during login. Please try again.",
        });
      } finally {
        setIsLoading(false);
        setShowProgress(false);
      }
    } else {
      setResponse({
        severity: "error",
        message: "Please enter username and password",
      });
      setIsLoading(false);
      setShowProgress(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3,
        }}
      />

      <Container maxWidth="sm">
        <Slide direction="up" in={true} timeout={800}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: 4,
                textAlign: "center",
                color: "white",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  margin: "0 auto 16px",
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "50%",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
              >
                <img 
                  src={logo} 
                  alt="Bharat Cars Logo" 
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "cover",
                    objectPosition: "center"
                  }} 
                />
              </Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  marginBottom: 1,
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                Bharat Self Drive Cars
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontSize: "1.1rem",
                }}
              >
                Admin Portal
              </Typography>
            </Box>

            {/* Login Form Section */}
            <CardContent sx={{ padding: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  marginBottom: 3,
                  color: "text.primary",
                  fontWeight: 600,
                }}
              >
                Welcome Back
              </Typography>

              {/* Alert Messages */}
              <Fade in={!!response}>
                <Box sx={{ marginBottom: 3 }}>
                  {response && (
                    <Alert
                      severity={response.severity}
                      variant="filled"
                      sx={{
                        borderRadius: 2,
                        "& .MuiAlert-message": {
                          fontSize: "0.95rem",
                        },
                      }}
                    >
                      {response.message}
                    </Alert>
                  )}
                </Box>
              </Fade>

              {/* Login Form */}
              <Box component="form" sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleLogin}
                  disabled={isLoading}
                  startIcon={<LoginIcon />}
                  sx={{
                    marginTop: 3,
                    marginBottom: 2,
                    padding: 1.5,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      boxShadow: "0 6px 25px rgba(102, 126, 234, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "rgba(0, 0, 0, 0.12)",
                      color: "rgba(0, 0, 0, 0.26)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </Button>
              </Box>

              {/* Footer */}
              <Divider sx={{ marginTop: 3, marginBottom: 2 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  fontSize: "0.85rem",
                }}
              >
                © 2024 Bharat Self Drive Cars. All rights reserved.
              </Typography>
            </CardContent>
          </Card>
        </Slide>
      </Container>

      {/* Loading Progress Bar */}
      {showProgress && (
        <LinearProgress
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 4,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        />
      )}
    </Box>
  );
};

export default LoginPage;
